/**
 * useAgentChatSession Hook - Clean Session-Scoped Interface
 *
 * Provides session-specific operations without global state dependencies:
 * - Gets session data directly from adapter
 * - Provides session-scoped sendMessage function
 * - True session isolation
 * - No currentSessionId dependencies
 */

import { useContext, useState, useCallback, useEffect, useMemo, useRef } from 'react';
import AgentSessionContext from '../context/AgentSessionContext';

/**
 * Session-scoped hook for interacting with a specific chat session
 * @param {string} sessionId - The session ID to manage
 * @returns {Object} Session interface with data and actions
 * @throws {Error} If used outside of AgentSessionProvider
 */
const useAgentChatSession = (sessionId) => {
  const context = useContext(AgentSessionContext);

  if (!context) {
    throw new Error(
      'useAgentChatSession must be used within an AgentSessionProvider. ' +
        'Make sure your component is wrapped with <AgentSessionProvider>.'
    );
  }

  if (!sessionId) {
    throw new Error('useAgentChatSession requires a sessionId parameter');
  }

  // Local state for session-specific loading/error states
  const [isSessionLoading, setIsSessionLoading] = useState(false);
  const [sessionError, setSessionError] = useState(null);

  // Track if we've already touched this session (prevent infinite loop)
  const hasTouchedRef = useRef(false);

  // TD-2571: has this session ever resolved during this mount? Deleting or archiving the open chat
  // removes it from the adapter map and fires a message update, which would otherwise flip the
  // verdict below to "unavailable" for the tick before the page navigates away.
  const hasResolvedRef = useRef(false);

  // ============================================================================
  // SESSION DATA (Via Provider Methods - Adapter Hidden)
  // ============================================================================

  // Get session data via provider methods (adapter is completely hidden)
  // TD-2571: also re-evaluate when the session list changes or the backend list settles. Keying
  // on messageUpdateTrigger alone cached the first-render `null` for the whole mount — nothing in
  // the list-fetch path bumps that counter — so the auto-load effect below never fired and the
  // chat sat on "Disconnected" forever.
  const session = useMemo(() => {
    if (import.meta.env.DEV) {
      console.log('🔄 Re-evaluating session data for trigger:', context.messageUpdateTrigger);
    }
    return context.getSession(sessionId);
  }, [sessionId, context.messageUpdateTrigger, context.allSessions, context.sessionsSettled]);

  const messages = useMemo(() => {
    if (import.meta.env.DEV) {
      console.log('🔄 Re-evaluating messages data for trigger:', context.messageUpdateTrigger);
    }
    const msgs = context.getMessages(sessionId);
    if (import.meta.env.DEV) {
      console.log('🔄 Found', msgs.length, 'messages for session:', sessionId);
    }
    return msgs;
  }, [sessionId, context.messageUpdateTrigger]);

  // ============================================================================
  // SESSION-SCOPED ACTIONS
  // ============================================================================

  const sendMessage = useCallback(
    async (content, options = {}) => {
      if (import.meta.env.DEV) {
        console.log('💬 Session-scoped sendMessage for session:', sessionId);
      }

      try {
        setSessionError(null);
        // Use provider's sendMessage method with session ID
        await context.sendMessage(content, sessionId, options);
      } catch (error) {
        console.error('❌ Failed to send message to session:', sessionId, error);
        setSessionError(error.message);
        throw error;
      }
    },
    [sessionId, context.sendMessage]
  );

  // TD-2347: session-scoped stop for the in-flight turn
  const stopMessage = useCallback(() => {
    context.stopMessage(sessionId);
  }, [sessionId, context.stopMessage]);

  // TD-2293: session-scoped hidden control reply (reveal confirm/cancel/poll).
  // Scoping matters — the agent resolves the quoted contacts from THIS
  // session's history, so a control reply must never target another session.
  const sendControlReply = useCallback(
    async (content) => {
      try {
        await context.sendControlReply(content, sessionId);
      } catch (error) {
        console.error('❌ Failed to send control reply to session:', sessionId, error);
        setSessionError(error.message);
        throw error;
      }
    },
    [sessionId, context.sendControlReply]
  );

  const loadSession = useCallback(async () => {
    if (!session) {
      console.error('❌ Session not found in adapter:', sessionId);
      setSessionError(`Session ${sessionId} not found`);
      return;
    }

    if (import.meta.env.DEV) {
      console.log('📂 Loading session via context:', sessionId);
    }
    setIsSessionLoading(true);
    setSessionError(null);

    try {
      // Use context's loadSession to update UI state
      await context.loadSession(sessionId);
      setIsSessionLoading(false);
    } catch (error) {
      console.error('❌ Failed to load session:', sessionId, error);
      setSessionError(error.message);
      setIsSessionLoading(false);
      throw error;
    }
  }, [sessionId, session, context.loadSession]);

  const clearSessionError = useCallback(() => {
    setSessionError(null);
  }, []);

  const retryMessage = useCallback(
    async (messageId) => {
      if (!context.retryMessage) {
        console.warn('⚠️ Retry functionality not available in current adapter');
        return;
      }

      try {
        setSessionError(null);
        await context.retryMessage(sessionId, messageId);
      } catch (error) {
        console.error('❌ Failed to retry message:', error);
        setSessionError(error.message);
        throw error;
      }
    },
    [sessionId, context.retryMessage]
  );

  // ============================================================================
  // AUTO-LOAD SESSION ON MOUNT
  // ============================================================================

  useEffect(() => {
    // Auto-load session if it exists but isn't active in UI
    if (session && context.currentSessionId !== sessionId) {
      if (import.meta.env.DEV) {
        console.log('🔄 Auto-loading session on mount:', sessionId);
      }
      loadSession().catch((error) => {
        console.error('Failed to auto-load session:', error);
      });
    }

    // Touch session to update lastActivity (for "most recently opened" ordering)
    // Only touch ONCE per session to prevent infinite loop (touchSession triggers messageUpdateTrigger)
    if (session && context.touchSession && !hasTouchedRef.current) {
      hasTouchedRef.current = true;
      if (import.meta.env.DEV) {
        console.log('👆 Touching session to update lastActivity:', sessionId);
      }
      context.touchSession(sessionId);
    }
  }, [sessionId, session, context.currentSessionId, loadSession, context.touchSession]);

  // Reset per-session tracking when sessionId changes
  useEffect(() => {
    hasTouchedRef.current = false;
    hasResolvedRef.current = false;
  }, [sessionId]);

  // TD-2571: remember that this session resolved at least once. Written in an effect rather than
  // during render so StrictMode's double-render cannot latch it early.
  useEffect(() => {
    if (session) {
      hasResolvedRef.current = true;
    }
  }, [session]);

  // ============================================================================
  // COMPUTED VALUES
  // ============================================================================

  const isSessionActive = context.currentSessionId === sessionId;
  const isConnected = context.connectionStatus === 'connected' && isSessionActive;

  // TD-2571: the thread is genuinely gone — the backend list answered, it got through, and the
  // session is not in it. Anything short of that is "we don't know yet" and must not be reported
  // as a verdict: a failed settle keeps the retry path (sessionError), a session that resolved
  // earlier this mount is mid-navigation after a delete/archive, not missing.
  const sessionUnavailable =
    !session && context.sessionsSettled && context.sessionsSettledOk && !hasResolvedRef.current;

  // The list settled but we never reached the backend, so we cannot claim the thread is missing.
  const sessionCheckFailed =
    !session && context.sessionsSettled && !context.sessionsSettledOk && !hasResolvedRef.current;

  // TD-1904: Per-session getters; TD-1911: idle check for busy guard
  const canSendMessage =
    isConnected
    && !context.getSessionIsSending(sessionId)
    && !isSessionLoading
    && context.getSessionAgentState(sessionId) === 'idle';

  // ============================================================================
  // RETURN INTERFACE
  // ============================================================================

  return {
    // Session Data (from adapter)
    session,
    messages,
    sessionExists: !!session,

    // Session Actions (scoped to this session)
    sendMessage,
    stopMessage,
    sendControlReply,
    loadSession,
    retryMessage,
    clearSessionError,
    clearError: context.clearError, // Global error clearing

    // Session State
    isSessionActive,
    isConnected,
    sessionUnavailable,
    sessionCheckFailed,
    canSendMessage,
    isSessionLoading,
    sessionError,

    // UI State (from context) - TD-1904: per-session getters
    agentState: context.getSessionAgentState(sessionId),
    connectionStatus: context.connectionStatus,
    isLoading: context.isLoading || isSessionLoading,
    isSending: context.getSessionIsSending(sessionId),
    error: context.error || sessionError,
    searchMetadata: context.getSessionSearchMetadata(sessionId),
    searchProgress: context.getSessionSearchProgress(sessionId),

    // Computed Properties
    sessionId,
    sessionTitle: session?.title || 'Unknown Session',
    messageCount: messages?.length || 0
  };
};

export default useAgentChatSession;
